import { FormEvent } from "react";
import { Controller, useFormContext } from "react-hook-form";

import Box from "ds/components/Box";
import Input from "ds/components/Input";
import Typography from "ds/components/Typography";
import FormToggleField from "ds/components/Form/ToggleField";
import FormField from "ds/components/Form/Field";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import Tooltip from "ds/components/Tooltip";
import useTypedContext from "hooks/useTypedContext";
import Button from "ds/components/Button";
import { TooltipReferenceProps } from "ds/components/Tooltip/types";
import useTaskCreate from "shared/Stack/useTaskCreate";
import FlashContext from "components/FlashMessages/FlashContext";

type StackTaskFormProps = {
  accessErrorMessage?: string;
  stackId: string;
  setNextUrl: (nextUrl: string) => void;
};

const StackTaskForm = ({ accessErrorMessage, stackId, setNextUrl }: StackTaskFormProps) => {
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = useFormContext();

  const { reportSuccess } = useTypedContext(FlashContext);

  const [createTask, { loading }] = useTaskCreate();

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (accessErrorMessage) {
      return;
    }

    handleSubmit((formData) => {
      if (stackId && formData.commandValue) {
        createTask(
          {
            stack: stackId,
            command: formData.commandValue,
            skipInitialization: formData.skipInitialization,
          },
          (data) => {
            if (data?.taskCreate.id) {
              const taskId = data.taskCreate.id;
              reportSuccess({ message: `Task ${taskId} successfully triggered` });
              setNextUrl(`/stack/${stackId}/run/${taskId}`);
            }
          }
        );
      }
    })();

    return false;
  };

  return (
    <form onSubmit={onSubmit}>
      <Box direction="column" gap="medium" margin="0 0 x-large 0" fullWidth>
        <Box gap="medium" align="start">
          <FormField error={errors?.commandValue?.message as string} fullWidth noMargin>
            {({ ariaInputProps }) => (
              <Input
                type="text"
                placeholder="Command"
                error={!!errors?.commandValue?.message}
                disabled={!!accessErrorMessage}
                {...register("commandValue", {
                  validate: (value) => (value?.trim() ? true : "Command field is required."),
                })}
                {...ariaInputProps}
              />
            )}
          </FormField>

          <Tooltip
            active={!!accessErrorMessage}
            on={(props: TooltipReferenceProps) => (
              <Button
                {...props}
                disabled={!stackId || !!accessErrorMessage}
                loading={loading}
                type="submit"
                variant="primary"
              >
                Perform
              </Button>
            )}
          >
            {accessErrorMessage}
          </Tooltip>
        </Box>

        <Box>
          <Controller
            name="skipInitialization"
            control={control}
            render={({ field }) => (
              <FormToggleField
                variant="checkbox"
                onChange={field.onChange}
                checked={field.value}
                disabled={!!accessErrorMessage}
                title="Skip workspace initialization"
                tooltipInfo={
                  <>
                    <TooltipModalTitle>Skip workspace initialization</TooltipModalTitle>
                    <TooltipModalBody align="start">
                      <Typography tag="p" variant="p-body3">
                        Skip workspace initialization before this task runs. This means pre-init
                        hooks and vendor-specific setup will not be executed. Leave unchecked to
                        follow the default flow.
                      </Typography>
                    </TooltipModalBody>
                  </>
                }
              />
            )}
          />
        </Box>
      </Box>
    </form>
  );
};

export default StackTaskForm;
